import { useKpisPerimeter, type FiltersTreeType } from '#composables/kpisPerimeter';
import { useLanguage } from '#composables/useLanguage';
import { filterToTreeViewItem } from '#domain/core/filter/filter.mapper';
import { listIntegratedFranchised } from '#domain/core/filter/filter.query';
import { useUserStore } from '#stores/user.store';
import {
  PERIOD_TO_LOOKER_PERIOD,
  extractFilterKeyFromNodeId,
  isNielsenInterval,
  type ProductTaxonomyType
} from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { computed, unref, type Ref } from 'vue';

interface UseIntegratedFranchisedDataProps {
  debouncedSearch: Readonly<Ref<string>>;
  enabled: Ref<boolean>;
  checkedItemsState: Ref<FiltersTreeType>;
  taxonomy: Ref<ProductTaxonomyType>;
}

export function useIntegratedFranchisedData({
  debouncedSearch,
  enabled,
  checkedItemsState,
  taxonomy
}: UseIntegratedFranchisedDataProps) {
  const { t } = useLanguage();
  const userStore = useUserStore();
  const { userCountry } = storeToRefs(userStore);

  const {
    period: { selectedPeriod },
    storePerimeter: { storePerimeter }
  } = useKpisPerimeter();

  const periodField = PERIOD_TO_LOOKER_PERIOD[selectedPeriod.value.period];

  const {
    data: integratedFranchisedData,
    isLoading,
    isFetching
  } = useQuery({
    enabled,
    queryKey: [listIntegratedFranchised.name, debouncedSearch, taxonomy ?? taxonomy, selectedPeriod, storePerimeter],
    queryFn: async () => {
      const unrefTaxonomy = unref(taxonomy);

      if (
        unrefTaxonomy !== 'nielsen' &&
        periodField !== 'period_code' &&
        !isNielsenInterval(selectedPeriod.value.periodInterval) &&
        storePerimeter.value === 'likeForLike'
      ) {
        return listIntegratedFranchised({
          taxonomy: unrefTaxonomy,
          storePerimeter: unref(storePerimeter),
          period: {
            field: periodField,
            value: selectedPeriod.value.periodInterval
          }
        });
      }

      return listIntegratedFranchised({
        taxonomy: unref(taxonomy),
        storePerimeter: 'allStores'
      });
    },
    select: (result) => {
      if (result.status === 'error') {
        return undefined;
      }

      return result.data;
    }
  });
  const itemsIntegratedFranchised = computed(() => {
    if (!integratedFranchisedData.value) {
      return undefined;
    }
    const integratedFranchisedFiltered = integratedFranchisedData.value?.children?.filter(
      (i) =>
        i.label.includes(debouncedSearch.value) ||
        checkedItemsState.value.integratedFranchisedFilters[i.nodeId]?.state === 'checked'
    );

    const integratedFranchised = {
      ...integratedFranchisedData.value,
      children: integratedFranchisedFiltered?.map((item) => {
        const filterKey = extractFilterKeyFromNodeId(item.nodeId);
        const label =
          userCountry.value === 'ESP' && filterKey === '2'
            ? t('filterPopin.integrateFranchisedData.2ESP')
            : t(`filterPopin.integrateFranchisedData.${filterKey}`);
        return { ...item, label };
      })
    };
    return filterToTreeViewItem(integratedFranchised, checkedItemsState.value.integratedFranchisedFilters);
  });

  const isLoadingIntegratedFranchised = computed(() => {
    // on tanstack query v4, there is a [known issue](https://github.com/TanStack/query/issues/3975) that
    // isLoading and isStale stay true if `enabled` is false.
    return isLoading.value && isFetching.value;
  });

  return { itemsIntegratedFranchised, isLoadingIntegratedFranchised };
}
